import { ref, computed } from 'vue'
import enEN from './locales/en-en.js'
import nlNL from './locales/nl-nl.js'

// ── Locale registry — SINGLE SOURCE OF TRUTH ──
// To add a language (e.g. French): create src/i18n/locales/fr-fr.js, import it,
// and add one entry here. The route (/fr-fr), the switcher dropdown, and the
// translations all derive from this list automatically.
export const LOCALES = [
  { code: 'en-en', label: 'English', flag: '🇬🇧', messages: enEN },
  { code: 'nl-nl', label: 'Nederlands', flag: '🇳🇱', messages: nlNL }
  // { code: 'fr-fr', label: 'Français', flag: '🇫🇷', messages: frFR },
]

export const DEFAULT_LOCALE = 'en-en'
const STORAGE_KEY = 'custus.locale'

export function isValidLocale(code) {
  return LOCALES.some((l) => l.code === code)
}
export function localeCodes() {
  return LOCALES.map((l) => l.code)
}
export function getLocaleMeta(code) {
  return LOCALES.find((l) => l.code === code) || LOCALES.find((l) => l.code === DEFAULT_LOCALE)
}

// Current active locale (reactive)
const current = ref(DEFAULT_LOCALE)
export const currentLocale = computed(() => current.value)

export function setLocale(code) {
  if (!isValidLocale(code)) return
  current.value = code
  try { localStorage.setItem(STORAGE_KEY, code) } catch { /* ignore */ }
  if (typeof document !== 'undefined') document.documentElement.lang = code.split('-')[0]
}

/**
 * Decide which locale to use the FIRST time, in priority order:
 *   1. explicit route locale (e.g. /nl-nl) — handled by the caller
 *   2. a previously chosen locale (localStorage) — the user's own choice wins
 *   3. the browser / device language (location-ish signal)
 *   4. the default (English)
 * Returns a valid locale code.
 */
export function detectInitialLocale() {
  // 2) stored choice
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && isValidLocale(saved)) return saved
  } catch { /* ignore */ }

  // 3) browser languages → match on the language part (nl, en, …)
  const langs = (typeof navigator !== 'undefined' && navigator.languages) || []
  for (const l of langs) {
    const short = String(l).toLowerCase().split('-')[0]
    const hit = LOCALES.find((x) => x.code.split('-')[0] === short)
    if (hit) return hit.code
  }
  return DEFAULT_LOCALE
}

// ── translator ──
function lookup(messages, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), messages)
}

/**
 * Reactive translate. Usage: t('nav.pricing'). Falls back to English, then to
 * the key itself, so a missing translation never blanks the UI.
 */
export function t(key) {
  const meta = getLocaleMeta(current.value)
  const val = lookup(meta.messages, key)
  if (val !== undefined) return val
  const fallback = lookup(getLocaleMeta(DEFAULT_LOCALE).messages, key)
  return fallback !== undefined ? fallback : key
}

// Convenience composable for components
export function useI18n() {
  return {
    t: (key) => {
      // touch `current` so computed/render re-runs on locale change
      void current.value
      return t(key)
    },
    locale: currentLocale,
    locales: LOCALES,
    setLocale
  }
}
